import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import FollowTechniaHQ from "../components/FollowTechniaHQ";
import SocialIcon from "../components/SocialIcon";
import { brandIdentity, buildOrganizationSchema } from "../data/brand";
import { techniaSocialLinks } from "../data/socialLinks";
import { seoConfig } from "../data/seoConfig";

const coverageLinks = [
  ["Humanoid robots", "/humanoid-robots"],
  ["Physical AI", "/physical-ai"],
  ["Robotics companies", "/robotics-companies"],
  ["Robotics software", "/robotics-software"],
  ["Robot components", "/robot-components"],
  ["Robot sensors", "/robot-sensors"],
  ["Robot types", "/robot-types"],
  ["Robotics data collection", "/robotics-data-collection"],
  ["Robotics news", "/robotics-news"],
  ["AI news", "/ai-news"],
  ["Robotics events", "/robotics-events"],
];

const coverageAreas = [
  ["Humanoid robotics", "New humanoids, locomotion, balance, manipulation, hands, teleoperation, control systems, deployments and measurable limitations."],
  ["Physical AI and Embodied AI", "Vision language action models, robot foundation models, world models, imitation learning, reinforcement learning, simulation and real robot evaluation."],
  ["Industrial and logistics robotics", "Factory robots, warehouse systems, autonomous mobile robots, manipulation cells and robot deployments with a clear task boundary."],
  ["Quadrupeds and mobile systems", "Robot dogs, inspection platforms, field robots and other mobile systems where terrain, sensing, payload and control method affect performance."],
  ["Dexterous manipulation and components", "Robot hands, grippers, actuators, motors, reducers, cameras, depth sensors, tactile sensing and the hardware that shapes physical capability."],
  ["Companies, research and events", "Startups, established manufacturers, university laboratories, scientific publications, prototypes, conferences, exhibitions and robot competitions."],
];

const regions = [
  ["China", "Companies, local startups, Beijing and Shanghai events, university work, embodied AI programs and fast moving humanoid deployments."],
  ["Japan", "Humanoid research, service robots, university spinouts, industrial robotics and local robotics exhibitions."],
  ["South Korea", "Humanoid platforms, university linked robotics companies, industrial groups and Physical AI research."],
  ["Taiwan", "Robotics startups, electronics linked robotics programs and emerging Physical AI companies."],
  ["United States", "Humanoid startups, industrial deployments, research laboratories, robot learning and Physical AI companies."],
  ["Europe", "Country level coverage of humanoid startups, industrial robotics, research projects, component suppliers and robotics events."],
];

const networkLinks = [
  {
    id: "link",
    name: "Website",
    url: "https://www.techniahqservice.com",
    description: "TechniaHQRobot website",
  },
  ...techniaSocialLinks,
];

export default function About() {
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "About TechniaHQRobot", to: "/about-techniahq" },
  ];
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "About TechniaHQRobot",
    description: brandIdentity.description,
    url: `${seoConfig.baseUrl}/about-techniahq`,
    dateModified: "2026-09-03",
    mainEntity: { "@id": `${seoConfig.baseUrl}/#organization` },
  };

  return (
    <>
      <SEO
        title="About TechniaHQRobot Independent Robotics and Physical AI Media"
        description="About TechniaHQRobot, an independent robotics media publication covering humanoid robots, Physical AI, Embodied AI, robotics companies, research and events."
        path="/about-techniahq"
        schema={[buildBreadcrumbSchema(breadcrumbs), buildOrganizationSchema(), pageSchema]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="Independent Robotics Media"
        title="About TechniaHQRobot"
        description="TechniaHQRobot is an independent robotics media publication focused on humanoid robots, Physical AI, Embodied AI, robot hardware, research, companies and real world deployments."
      />

      <div className="site-shell grid gap-7 py-10 sm:py-14">
        <section className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="article-panel">
            <h2 className="text-3xl font-black tracking-tight text-neutral-950">Our coverage</h2>
            <p className="mt-5 text-base leading-8 text-neutral-700">
              TechniaHQRobot reports on humanoid robots, industrial and service robots, robot hardware, embodied AI, Physical AI, teleoperation, robot learning data, software, companies and real world demonstrations. Coverage focuses on the machine, control method, sensors, actuators, task conditions, limitations and evidence behind each claim.
            </p>
            <p className="mt-4 text-base leading-8 text-neutral-700">
              The reporting also follows logistics robotics, quadruped robots, dexterous hands, robotic grippers, robot components, startups, scientific research, conferences and competitions. New actuators, motors, sensors and control models are included when they materially change what a robot can do.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {coverageLinks.map(([label, to]) => <Link key={to} to={to} className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-black text-neutral-800 hover:border-blue-200 hover:text-blue-700">{label}</Link>)}
            </div>
          </div>
          <aside className="rounded-[1.75rem] border border-blue-100 bg-blue-50 p-6 sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Editorial mission</p>
            <h2 className="mt-4 text-2xl font-black tracking-tight text-neutral-950">Make robot capability legible</h2>
            <p className="mt-4 text-base leading-8 text-neutral-700">
              A polished video can hide teleoperation, scripting, resets and failed attempts. TechniaHQRobot separates visible evidence from inference and states when autonomy, latency, reliability or deployment scale has not been independently confirmed.
            </p>
          </aside>
        </section>

        <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {coverageAreas.map(([title, text]) => (
            <article key={title} className="soft-card p-6 sm:p-7">
              <h2 className="text-xl font-black text-neutral-950">{title}</h2>
              <p className="mt-4 text-sm leading-7 text-neutral-700">{text}</p>
            </article>
          ))}
        </section>

        <section className="article-panel">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Global Robotics Coverage</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-neutral-950">Regional reporting with deeper attention to Asian robotics</h2>
          <p className="mt-5 max-w-4xl text-base leading-8 text-neutral-700">
            TechniaHQRobot follows robotics globally with particular attention to China, Japan, South Korea and Taiwan. Local company pages, government announcements, university sources and event organizers are used when they provide information that is absent from English language coverage. The site also follows major robotics companies and research in the United States and individual European countries.
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {regions.map(([region, text]) => (
              <article key={region} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5">
                <h3 className="text-lg font-black text-neutral-950">{region}</h3>
                <p className="mt-3 text-sm leading-7 text-neutral-700">{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-2">
          <article className="soft-card p-6 sm:p-8">
            <h2 className="text-2xl font-black tracking-tight text-neutral-950">Humanoid robotics focus</h2>
            <p className="mt-4 text-base leading-8 text-neutral-700">Humanoid coverage examines locomotion, balance, fall recovery, hands, joint modules, perception, teleoperation, autonomous control, safety, uptime and whether a task can be repeated outside a prepared demonstration.</p>
          </article>
          <article className="soft-card p-6 sm:p-8">
            <h2 className="text-2xl font-black tracking-tight text-neutral-950">Physical AI focus</h2>
            <p className="mt-4 text-base leading-8 text-neutral-700">Physical AI coverage follows robot foundation models, vision language action systems, world models, demonstrations, simulation, data annotation and closed loop control without confusing benchmark results with unrestricted real world autonomy.</p>
          </article>
        </section>

        <section className="article-panel">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Editorial Approach</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-neutral-950">How claims are verified</h2>
          <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">
            Reporting starts with the strongest available source. Official company pages, technical documentation, scientific publications, universities, event organizers, government documents, repositories and technical presentations are preferred when they directly support the claim being reported.
          </p>
          <ol className="mt-6 grid gap-4 md:grid-cols-2">
            {[
              ["Trace the primary source", "Use company documentation, official project pages, papers, repositories, benchmark pages, event organizers and public technical material before secondary summaries."],
              ["Identify the control method", "State whether evidence shows autonomy, teleoperation, scripted choreography, motion capture, simulation or a mixed control stack."],
              ["Separate measurement from appearance", "Do not infer latency, success rate, runtime, payload or production readiness from a short video when the metric is not published."],
              ["Record limitations", "Describe unknown conditions, evaluation scope, missing reproduction, hardware constraints and the difference between a demonstration and a deployment."],
            ].map(([title, text], index) => (
              <li key={title} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Step {index + 1}</p>
                <h3 className="mt-2 text-lg font-black text-neutral-950">{title}</h3>
                <p className="mt-3 text-sm leading-7 text-neutral-700">{text}</p>
              </li>
            ))}
          </ol>
          <Link to="/editorial-policy" className="mt-6 inline-flex rounded-full border border-neutral-300 bg-white px-5 py-3 text-sm font-black text-neutral-800 hover:border-blue-300 hover:text-blue-700">Read the Editorial Policy</Link>
        </section>

        <section className="grid gap-5 lg:grid-cols-3">
          {[
            ["Reporting", "Fact based coverage of a launch, paper, demonstration, deployment or public result. Sources and uncertainty are stated in the article."],
            ["Analysis", "Technical interpretation that connects visible evidence with known robotics methods. Inferences are labeled and kept separate from confirmed facts."],
            ["Sponsored content", "Paid work is disclosed. Compensation does not turn an unverified capability into a fact, and technical limitations remain part of the coverage."],
          ].map(([title, text]) => (
            <article key={title} className="soft-card p-6">
              <h2 className="text-xl font-black text-neutral-950">{title}</h2>
              <p className="mt-4 text-sm leading-7 text-neutral-700">{text}</p>
            </article>
          ))}
        </section>

        <section className="grid gap-5 lg:grid-cols-2">
          <article className="article-panel">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Who the site is for</p>
            <h2 className="mt-3 text-2xl font-black tracking-tight text-neutral-950">Readers who need the mechanism, not only the clip</h2>
            <p className="mt-4 text-base leading-8 text-neutral-700">TechniaHQRobot is written for robotics followers, engineers, researchers, founders, investors, manufacturers and general readers who want to understand what a machine actually did. Articles favor named robots, sensors, actuators, control methods, test conditions and visible failure points over broad claims.</p>
          </article>
          <article className="article-panel">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Independent Robotics Media</p>
            <h2 className="mt-3 text-2xl font-black tracking-tight text-neutral-950">Independent from the companies being covered</h2>
            <p className="mt-4 text-base leading-8 text-neutral-700">TechniaHQRobot is not the official website of Tesla, NVIDIA, Unitree, Figure, Boston Dynamics or another robotics company mentioned in its coverage. Company names, robot names and logos belong to their respective owners. Paid collaborations are identified and remain subject to the same evidence rules.</p>
          </article>
        </section>

        <section className="article-panel">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">TechniaHQRobot Network</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-neutral-950">Official website and social platforms</h2>
          <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">
            These are the official TechniaHQRobot destinations. External platform links open in a new tab.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {networkLinks.map((item) => (
              <a
                key={`${item.name}-${item.url}`}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open TechniaHQRobot on ${item.name}`}
                className="focus-ring flex items-center gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-4 text-sm font-black text-neutral-800 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-neutral-200 bg-white">
                  <SocialIcon id={item.id} className="h-5 w-5" />
                </span>
                <span>{item.name}</span>
              </a>
            ))}
          </div>
        </section>

        <section className="article-panel">
          <h2 className="text-3xl font-black tracking-tight text-neutral-950">Corrections and source challenges</h2>
          <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">A reader, researcher or company can challenge a specification, attribution, date, autonomy claim or image credit. Send the exact page URL, the disputed sentence and a primary source. Confirmed errors are corrected in the article. A disagreement with analysis is reviewed, but it does not automatically require removal.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="rounded-full bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700">Send a correction</Link>
            <Link to="/terms-of-use" className="rounded-full border border-neutral-300 bg-white px-5 py-3 text-sm font-black text-neutral-800 hover:border-blue-300 hover:text-blue-700">Read the Terms of Use</Link>
          </div>
        </section>

        <section className="rounded-[1.75rem] border border-blue-100 bg-blue-50 p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Contact</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-neutral-950">Contact TechniaHQRobot</h2>
          <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">
            Robotics companies, startups, researchers, universities, event organizers, PR agencies and technology companies can contact TechniaHQRobot about technical information, launches, research, events, corrections, advertising and partnerships.
          </p>
          <p className="mt-4 text-base leading-8 text-neutral-700">
            Email <a className="font-black text-blue-700 underline underline-offset-4" href={`mailto:${brandIdentity.email}`}>{brandIdentity.email}</a> or use the contact page.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="rounded-full bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700">Contact</Link>
            <Link to="/partnerships" className="rounded-full border border-neutral-300 bg-white px-5 py-3 text-sm font-black text-neutral-800 hover:border-blue-300 hover:text-blue-700">Partnerships</Link>
            <Link to="/advertise" className="rounded-full border border-neutral-300 bg-white px-5 py-3 text-sm font-black text-neutral-800 hover:border-blue-300 hover:text-blue-700">Advertise</Link>
          </div>
        </section>

        <section className="article-panel">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-neutral-500">Editorial organization</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-neutral-950">TechniaHQRobot</h2>
          <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">Articles are published under the TechniaHQRobot editorial organization. Corrections and sourcing questions can be sent to <a className="font-black text-blue-700 underline underline-offset-4" href={`mailto:${brandIdentity.email}`}>{brandIdentity.email}</a>.</p>
          <p className="mt-4 text-sm font-bold text-neutral-500">Last updated September 3, 2026</p>
        </section>
      </div>

      <FollowTechniaHQ />
    </>
  );
}
